// src/hooks/useItinerary.js
// App.js에서 일정 생성/수정/저장/공유/체크리스트/관광 데이터 관련 로직을 추출한 커스텀 훅.
// 로직·프롬프트·에러 메시지·Firebase 경로는 App.js 원본 그대로 verbatim 유지.
//
// 시그니처에 명시된 props 외에, 추출된 함수가 사용하는 "부모 소유" 의존성은 추가 prop으로 전달받는다.
//   - setStep, setView : 앱 네비게이션 상태 (부모 소유)
//   - setPersonal, setTravel : 프로필/여행 상태 setter (loadSavedTripToResult에서 사용)
//   - upsertRanking : 랭킹 반영 함수 (generateItinerary에서 사용)
//   - setCommunityTrips, setSharedTripIds : 커뮤니티 상태 setter (공유 함수에서 사용)
// 관광(TourAPI) 표시 상태(tourItems 등)는 fetchTourAPI가 직접 세팅하므로 훅이 소유하고 return으로 노출한다.
import { useState, useRef, useEffect, useCallback } from "react";
import {
  doc, setDoc, deleteDoc, collection, addDoc,
  getDocs,
} from "../services/firebase";
import { geminiCall } from "../services/aiService";
import { sanitizeItinerary, hasChinese } from "../utils/textUtils";
import { GYENAM_CITIES } from "../constants/formOptions";

function buildMbtiInstruction(mbti) {
  const code = String(mbti || "").trim().toUpperCase();
  if (!/^[IE][SN][TF][JP]$/.test(code)) return "";

  const axisInstructions = [
    code[0] === "I"
      ? "- I(내향): 혼잡도가 낮은 시간대와 한적한 장소를 우선하고, 하루 장소 수를 기본 가이드보다 1곳 줄이며 혼자 숨 돌릴 휴식 시간을 확보"
      : "- E(외향): 활기찬 대표 명소·시장·야시장과 현지인 교류 또는 참여형 체험을 포함",
    code[1] === "S"
      ? "- S(감각): 직접 보고 맛보고 체험하는 활동을 우선하고, 운영시간·대표 메뉴·이동 방법처럼 바로 실행할 수 있는 정보를 구체적으로 제시"
      : "- N(직관): 지역의 이야기·예술·건축·새로운 콘셉트를 연결한 테마형 동선을 만들고, 색다른 장소를 한 곳 이상 포함",
    code[2] === "T"
      ? "- T(사고): 장소 선택 이유를 시간·거리·비용·동선 효율 중심으로 설명하고, 비슷한 선택지는 장단점을 비교"
      : "- F(감정): 분위기·정서적 만족·동행 경험을 고려한 장소를 우선하고, 기억에 남을 감성 포인트와 편안한 휴식을 포함",
    code[3] === "J"
      ? "- J(판단): 여유로운 페이스는 유지하되 시간대별 계획을 명확히 고정하고 예약 필요 여부·권장 예약 시점·사전 확인 항목을 빠짐없이 표시"
      : "- P(인식): 일정 사이에 조정 가능한 여유 슬롯을 두고, 날씨·혼잡·기분에 따라 바꿀 수 있는 가까운 대안 옵션을 함께 제시",
  ];

  return `\n[MBTI 기반 여행 스타일: ${code}]
아래 지시는 MBTI 네 축을 여행 선호로 해석한 개인화 기준이며, 장소 선택·일정 밀도·정보 제시에 실제로 반영하세요.
  ${axisInstructions.join("\n")}`;
}

function buildBloodTypeInstruction(bloodType) {
  const code = String(bloodType || "").trim().toUpperCase();
  const toneByType = {
    "A형": "차분하고 정돈된 공간, 섬세하고 안정적인 추천 어조",
    "B형": "호기심을 자극하는 개성 있는 공간, 자유롭고 경쾌한 추천 어조",
    "O형": "활기와 개방감이 느껴지는 공간, 따뜻하고 자신감 있는 추천 어조",
    "AB형": "고요함과 독특함이 공존하는 공간, 감각적이고 사려 깊은 추천 어조",
  };
  return toneByType[code] ? `- 혈액형 ${code}: ${toneByType[code]}` : "";
}

function buildZodiacInstruction(zodiac) {
  const name = String(zodiac || "").trim().split(" ")[0];
  const elementGroups = [
    { signs: ["양자리", "사자자리", "사수자리"], tone: "생동감·모험·선명한 포인트가 있는 테마" },
    { signs: ["황소자리", "처녀자리", "염소자리"], tone: "편안함·미감·완성도 높은 디테일이 있는 테마" },
    { signs: ["쌍둥이자리", "천칭자리", "물병자리"], tone: "도시적 감각·다양성·가벼운 발견이 있는 테마" },
    { signs: ["게자리", "전갈자리", "물고기자리"], tone: "정서적 여운·물가·은은한 분위기가 있는 테마" },
  ];
  const group = elementGroups.find(item => item.signs.includes(name));
  return group ? `- 별자리 ${name}: ${group.tone}` : "";
}

function buildPersonalityMoodInstruction(bloodType, zodiac) {
  const moodHints = [
    buildBloodTypeInstruction(bloodType),
    buildZodiacInstruction(zodiac),
  ].filter(Boolean);
  if (moodHints.length === 0) return "";

  return `\n[혈액형·별자리 분위기 연출 힌트]
아래 내용은 실증적 성향 판단이 아닌 연출 참고로만 사용하세요.
${moodHints.join("\n")}
- 장소의 분위기, 추천 문구의 어조, 날짜별 테마 색채와 '✦ 성향 한줄 분석'에만 가볍게 반영
- 동선·이동시간·영업시간·예산·안전·사용자 입력과 충돌하면 실용적 제약을 반드시 우선`;
}

export function useItinerary({
  personal, travel, travelNights, darkMode, lang, t, companionType,
  fbDb, fbAuth, firebaseUid, loggedInUser,
  hasRequiredConsents, requestRequiredConsent,
  // ── 부모 소유 의존성 (추출 함수가 verbatim으로 호출) ──
  setStep, setView, setPersonal, setTravel, upsertRanking,
  setCommunityTrips, setSharedTripIds,
}) {
  // ── 일정/장소 ──
  const [itinerary, setItinerary] = useState("");
  const [progress, setProgress] = useState(0);
  const [spots, setSpots] = useState([]);
  const [spotsProgress, setSpotsProgress] = useState(0); // 0~100
  const [spotsTotal, setSpotsTotal] = useState(0);
  const [spotsLoading, setSpotsLoading] = useState(false);
  const [selectedSpotIdx, setSelectedSpotIdx] = useState(0);
  const [error, setError] = useState("");
  const [resultView, setResultView] = useState("itinerary"); // "itinerary"|"map"|"checklist"

  // ── 일정 수정 채팅 ──
  const [chatMessages, setChatMessages] = useState([]); // {role:"user"|"ai", content:string}
  const [chatInput, setChatInput] = useState("");
  const [chatLoading, setChatLoading] = useState(false);
  const chatBottomRef = useRef(null);

  // ── 소셜 공유 ──
  const [tripSaved, setTripSaved] = useState(false);
  const [communityShared, setCommunityShared] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  // ── 저장된 여행 보관함 ──
  const [savedTrips, setSavedTrips] = useState([]);
  const [savedTripsLoaded, setSavedTripsLoaded] = useState(false);
  const [savedTripsError, setSavedTripsError] = useState("");
  const [tripSaveError, setTripSaveError] = useState("");
  const savedTripsRequestRef = useRef(0);

  // ── PDF ──
  const [pdfLoading, setPdfLoading] = useState(false);

  // ── 체크리스트 ──
  const [checklist, setChecklist] = useState({ packing:[], before:[], after:[] });
  const [checklistLoading, setChecklistLoading] = useState(false);
  const [checklistProgress, setChecklistProgress] = useState(0);

  // ── 경남 추천 (TourAPI) — fetchTourAPI가 세팅, generateItinerary가 읽음 ──
  const [tourItems, setTourItems] = useState([]);
  const [tourLoading, setTourLoading] = useState(false);
  const [tourLoaded, setTourLoaded] = useState({ festival:false, tourist:false, food:false });
  const [tourMapPins, setTourMapPins] = useState([]);
  const [tourError, setTourError] = useState("");
  const tourLoadedRef = useRef({ festival:false, tourist:false, food:false }); // 클로저 문제 방지

  // ── [Firebase] 로그인 사용자의 저장된 여행 복원 ──
  const loadSavedTrips = useCallback(async () => {
    const requestId = ++savedTripsRequestRef.current;
    const uid = firebaseUid || fbAuth.currentUser?.uid;
    if (!uid) {
      setSavedTrips([]);
      setSavedTripsLoaded(true);
      setSavedTripsError("");
      return true;
    }

    setSavedTripsLoaded(false);
    setSavedTripsError("");
    try {
      const snap = await getDocs(collection(fbDb, "users", uid, "trips"));
      if (requestId !== savedTripsRequestRef.current) return false;
      const restoredTrips = snap.docs
        .map(tripDoc => ({ id: tripDoc.id, ...tripDoc.data() }))
        .sort((a, b) => (Number(b.savedAt) || 0) - (Number(a.savedAt) || 0));
      setSavedTrips(restoredTrips);
      return true;
    } catch (e) {
      console.error("저장된 여행 로드 오류:", e);
      if (requestId === savedTripsRequestRef.current) {
        setSavedTripsError("저장된 여행을 불러오지 못했습니다. 네트워크 연결을 확인한 뒤 다시 시도해주세요.");
      }
      return false;
    } finally {
      if (requestId === savedTripsRequestRef.current) setSavedTripsLoaded(true);
    }
  }, [fbAuth, fbDb, firebaseUid]);

  useEffect(() => {
    loadSavedTrips();
  }, [loadSavedTrips]);

  // ── [Firebase] 현재 여행 저장 ──
  const saveCurrentTrip = async (itineraryOverride, spotsOverride) => {
    const itinToSave = itineraryOverride ?? itinerary;
    if (!itinToSave) return false;
    const uid = firebaseUid || fbAuth.currentUser?.uid;
    const tripId = Date.now().toString();
    const trip = {
      id: tripId,
      destination: travel.destination, startDate: travel.startDate,
      endDate: travel.endDate, name: personal.name,
      style: travel.style, transport: travel.transport,
      mbti: personal.mbti, bloodType: personal.bloodType,
      itinerary: itinToSave, spots: spotsOverride ?? spots, savedAt: Date.now()
    };
    setTripSaveError("");
    if (uid) {
      try {
        await setDoc(doc(fbDb, "users", uid, "trips", tripId), trip);
      } catch(e) {
        console.error("여행 저장 오류:", e);
        setTripSaveError("여행 일정을 보관함에 저장하지 못했습니다. 네트워크 연결을 확인한 뒤 다시 시도해주세요.");
        setTripSaved(false);
        return false;
      }
    }
    setSavedTrips(prev => [trip, ...prev.filter(savedTrip => savedTrip.id !== trip.id)]);
    setTripSaved(true);
    setTimeout(() => setTripSaved(false), 2500);
    return true;
  };

  // ── [Firebase] 저장된 여행 삭제 ──
  const deleteSavedTrip = async (idx) => {
    const trip = savedTrips[idx];
    const uid = firebaseUid || fbAuth.currentUser?.uid;
    setSavedTripsError("");
    if (uid && trip?.id) {
      try {
        await deleteDoc(doc(fbDb, "users", uid, "trips", trip.id));
      } catch(e) {
        console.error("여행 삭제 오류:", e);
        setSavedTripsError("저장된 여행을 삭제하지 못했습니다. 잠시 후 다시 시도해주세요.");
        return false;
      }
    }
    setSavedTrips(prev => prev.filter((_, i) => i !== idx));
    return true;
  };

  // ── 저장된 여행 불러오기 ──
  const loadSavedTripToResult = (trip) => {
    setItinerary(sanitizeItinerary(trip.itinerary || ""));
    setSpots(trip.spots || []);
    setTravel(prev => ({ ...prev, destination: trip.destination, startDate: trip.startDate, endDate: trip.endDate }));
    setPersonal(prev => ({ ...prev, name: trip.name }));
    setResultView("itinerary");
    setStep("result");
  };

  // ── [Firebase] 커뮤니티에 공유 ──
  // 보안: 작성자 uid 포함 → Firestore Rules에서 소유권 검증
  const shareToCommunity = async () => {
    if (!loggedInUser) { alert("로그인 후 커뮤니티에 공유할 수 있습니다."); return; }
    if (!itinerary) return;
    const authUid = fbAuth.currentUser?.uid;
    if (!authUid) { alert("인증 오류: 다시 로그인해주세요."); return; }
    if (!hasRequiredConsents()) {
      await requestRequiredConsent("커뮤니티 공유", shareToCommunity);
      return;
    }
    const trip = {
      id: Date.now().toString(),
      uid: authUid,
      destination: travel.destination, startDate: travel.startDate,
      endDate: travel.endDate, name: personal.name || "여행자",
      style: travel.style, transport: travel.transport,
      mbti: personal.mbti, likes: 0,
      itinerary, spots, sharedAt: Date.now()
    };
    try {
      const docRef = await addDoc(collection(fbDb, "community"), trip);
      setCommunityTrips(prev => [{ ...trip, _docId: docRef.id }, ...prev]);
    } catch(e) { console.error("커뮤니티 공유 오류:", e); }
  };

  // ── [Firebase] 보관함 여행을 커뮤니티에 공유 ──
  const shareSavedTripToCommunity = async (trip, tid) => {
    if (!trip?.itinerary) return;
    const authUid = fbAuth.currentUser?.uid;
    if (!authUid) { alert("인증 오류: 다시 로그인해주세요."); return; }
    if (!hasRequiredConsents()) {
      await requestRequiredConsent(
        "커뮤니티 공유",
        () => shareSavedTripToCommunity(trip, tid)
      );
      return;
    }
    const communityTrip = {
      id: Date.now().toString(),
      uid: authUid,
      destination: trip.destination, startDate: trip.startDate,
      endDate: trip.endDate, name: trip.name || personal.name || "여행자",
      style: trip.style, transport: trip.transport,
      mbti: trip.mbti || personal.mbti, likes: 0,
      itinerary: trip.itinerary, spots: trip.spots || [], sharedAt: Date.now()
    };
    try {
      const docRef = await addDoc(collection(fbDb, "community"), communityTrip);
      setCommunityTrips(prev => [{ ...communityTrip, _docId: docRef.id }, ...prev]);
      setSharedTripIds(prev => [...prev, tid]);
    } catch(e) { console.error("커뮤니티 공유 오류:", e); }
  };

  // ── AI 체크리스트 생성 ──
  const generateChecklist = async () => {
    if (!hasRequiredConsents()) {
      await requestRequiredConsent("AI 여행 체크리스트 생성", generateChecklist);
      return;
    }
    setChecklistLoading(true);
    setChecklistProgress(0);
    // 체크리스트 생성 진행 타이머
    let clProg = 0;
    const clTimer = setInterval(() => {
      const inc = clProg < 40 ? 5 + Math.random()*4
                : clProg < 70 ? 2.5 + Math.random()*2
                : clProg < 88 ? 1 + Math.random()*0.8
                : clProg < 95 ? 0.2 + Math.random()*0.2
                :                0.05;
      clProg = Math.min(clProg + inc, 99);
      setChecklistProgress(Math.round(clProg));
    }, 250);
    try {
      const systemCL = `당신은 한국어 전문 여행 준비 전문가입니다. 반드시 한국어로만 답변하세요. 중국어 절대 금지.
여행 정보를 분석해서 체크리스트를 JSON으로만 반환하세요.
형식:
{
  "packing": [{"emoji":"👗","text":"아이템명","note":"설명(옵션)","priority":"high|normal","checked":false}],
  "before": [{"emoji":"📱","text":"할일","note":"","priority":"high|normal","checked":false}],
  "after": [{"emoji":"🧺","text":"할일","note":"","priority":"normal","checked":false}]
}
각 카테고리 8~12개 항목. JSON만 출력, 마크다운 금지. text와 note는 반드시 한국어로 작성.`;
      const userCL = `여행 정보:
목적지: ${travel.destination}
기간: ${travel.startDate}~${travel.endDate} (${Math.round((new Date(travel.endDate)-new Date(travel.startDate))/864e5)+1}일)
여행 스타일: ${travel.style}
이동 수단: ${travel.transport}
계절: ${new Date(travel.startDate).getMonth()+1}월
이름: ${personal.name}, MBTI: ${personal.mbti||"미입력"}

위 여행을 위한 짐 챙기기(packing), 여행 전 할 일(before), 여행 후 할 일(after) 체크리스트를 JSON으로 생성해주세요.`;
      const rawCL = await geminiCall(userCL, systemCL, 1500);
      // JSON 블록만 추출 (마커/프롬프트 잔재 제거)
      const jsonMatch = rawCL.match(/\{[\s\S]*\}/);
      const raw = jsonMatch ? jsonMatch[0] : rawCL.replace(/```json|```/gi,"").trim();
      const parsed = JSON.parse(raw);
      // 구조 검증 — 올바른 배열이 아니면 폴백 처리
      const isValidArr = (a) => Array.isArray(a) && (a.length === 0 || (a[0] && typeof a[0].text === "string"));
      if (!isValidArr(parsed.packing) && !isValidArr(parsed.before) && !isValidArr(parsed.after)) {
        throw new Error("체크리스트 구조 오류");
      }
      setChecklist({
        packing: isValidArr(parsed.packing) ? parsed.packing : [],
        before:  isValidArr(parsed.before)  ? parsed.before  : [],
        after:   isValidArr(parsed.after)   ? parsed.after   : []
      });
    } catch(e) {
      console.error("Checklist error:", e);
      // 기본 체크리스트 폴백
      setChecklist({
        packing: [
          {emoji:"🪪",text:"신분증/여권",note:"필수 지참",priority:"high",checked:false},
          {emoji:"💳",text:"신용카드/현금",note:"예비 카드 포함",priority:"high",checked:false},
          {emoji:"👕",text:"의류 (여행 일수+1벌)",priority:"normal",checked:false},
          {emoji:"🧴",text:"세면도구",priority:"normal",checked:false},
          {emoji:"💊",text:"상비약",note:"소화제, 진통제",priority:"normal",checked:false},
          {emoji:"🔌",text:"충전기/보조배터리",priority:"high",checked:false},
          {emoji:"📱",text:"이어폰",priority:"normal",checked:false},
          {emoji:"🌂",text:"우산/우비",priority:"normal",checked:false},
        ],
        before: [
          {emoji:"🏨",text:"숙소 예약 확인",priority:"high",checked:false},
          {emoji:"🚌",text:"교통편 예약 확인",priority:"high",checked:false},
          {emoji:"📲",text:"여행 앱 설치",priority:"normal",checked:false},
          {emoji:"💰",text:"여행 경비 준비",priority:"high",checked:false},
          {emoji:"🏦",text:"해외카드 사용 설정",priority:"normal",checked:false},
          {emoji:"📷",text:"카메라/메모리카드 확인",priority:"normal",checked:false},
          {emoji:"🌐",text:"데이터 로밍/유심 확인",priority:"normal",checked:false},
          {emoji:"🏠",text:"귀가 방법 확인",priority:"normal",checked:false},
        ],
        after: [
          {emoji:"🧺",text:"세탁물 정리",priority:"normal",checked:false},
          {emoji:"🧳",text:"짐 풀기 및 정리",priority:"normal",checked:false},
          {emoji:"📸",text:"사진 백업",priority:"high",checked:false},
          {emoji:"💳",text:"카드 사용 내역 확인",priority:"normal",checked:false},
          {emoji:"⭐",text:"여행 후기 작성",note:"다른 여행자를 위해!",priority:"normal",checked:false},
          {emoji:"🎁",text:"기념품 전달",priority:"normal",checked:false},
          {emoji:"📝",text:"다음 여행 계획 메모",priority:"normal",checked:false},
        ]
      });
    }
    clearInterval(clTimer);
    setChecklistProgress(100);
    setChecklistLoading(false);
  };

  /* ── [TourAPI] 경남 관광정보 불러오기 ── */
  const fetchTourAPI = async (tabKey) => {
    if (tourLoadedRef.current[tabKey]) return; // ref로 클로저 문제 없이 체크
    tourLoadedRef.current = { ...tourLoadedRef.current, [tabKey]: true }; // 즉시 마킹
    setTourError("");       // 이전 에러 초기화
    setTourItems([]);       // 이전 데이터 초기화
    setTourLoading(true);
    // contentTypeId: 15=행사/축제, 12=관광지, 39=음식점
    try {
      // TourAPI 키는 서버(Vercel Serverless)에서 관리 — 클라이언트에 노출 안됨
      const res = await fetch(`/api/tourapi?tabKey=${encodeURIComponent(tabKey)}`, {
        method: "GET",
      });
      if (!res.ok) {
        const err = await res.json().catch(()=>({}));
        throw new Error(err.error || `서버 오류 (${res.status})`);
      }
      const data = await res.json();
      const list = data.items || [];
      setTourItems(list);
      tourLoadedRef.current = { ...tourLoadedRef.current, [tabKey]: true };
      setTourLoaded(prev => ({ ...prev, [tabKey]: true }));
      // 지도 핀용 관광지 데이터 저장
      if (tabKey === "tourist") {
        setTourMapPins(list.filter(i => i.mapy && i.mapx).map(i => ({
          title: i.title,
          lat: parseFloat(i.mapy),
          lng: parseFloat(i.mapx),
          addr: i.addr1,
          img: i.firstimage,
        })));
      }
    } catch(e) {
      if (process.env.NODE_ENV === "development") {
        const safeErrMsg = (e.message||"").replace(/serviceKey=[^&]+/g,"serviceKey=***");
        console.error("TourAPI 오류:", e.name, safeErrMsg);
      }
      const msg = e.message === "Failed to fetch"
        ? "네트워크 오류가 발생했습니다. 인터넷 연결을 확인해주세요."
        : "데이터를 불러오지 못했어요";
      setTourError(msg);
      // 실패 시 ref 리셋 (재시도 가능하게)
      tourLoadedRef.current = { ...tourLoadedRef.current, [tabKey]: false };
      setTourLoaded(prev => ({ ...prev, [tabKey]: false }));
    }
    setTourLoading(false);
  };

  /* Fast itinerary generation: short prompt + faster timer */
  async function generateItinerary() {
    if (!hasRequiredConsents()) {
      await requestRequiredConsent("AI 맞춤 여행 일정 생성", generateItinerary);
      return;
    }
    setStep("generating");
    setSpots([]);
    setSpotsProgress(0);
    setError("");
    setProgress(0);
    setSpotsLoading(true);

    // 타이머: 3분(180초) 기준, 빠르게 80%까지 올리고 이후 천천히
    let cur = 0;
    const timer = setInterval(() => {
      const inc = cur < 40 ? 1.8 + Math.random()*1.2   // 0~40%: 빠르게
                : cur < 70 ? 0.9 + Math.random()*0.6   // 40~70%: 중간
                : cur < 88 ? 0.35 + Math.random()*0.25 // 70~88%: 느리게
                : cur < 95 ? 0.10 + Math.random()*0.08 // 88~95%: 아주 느리게
                :             0.03 + Math.random()*0.02; // 95~99%: 거의 멈춤
      cur = Math.min(cur + inc, 99);
      setProgress(Math.round(cur));
    }, 250);

    // 선택 항목은 입력된 경우에만 포함
    const profile = [
      personal.mbti && `MBTI:${personal.mbti}`,
      personal.bloodType && `혈액형:${personal.bloodType}`,
      personal.zodiac && `별자리:${personal.zodiac.split(" ")[0]}`,
    ].filter(Boolean).join(" / ");

    const mbtiInstruction = buildMbtiInstruction(personal.mbti);
    const personalityMoodInstruction = buildPersonalityMoodInstruction(
      personal.bloodType,
      personal.zodiac
    );

    // 동반자 유형별 프롬프트 지시문
    const companionInstruction = companionType === "couple"
      ? `\n[여행 유형: 커플여행]
- 분위기 좋은 레스토랑, 루프탑 카페, 감성 카페 우선 추천
- 둘만의 프라이빗한 공간(야경 명소, 한적한 산책로, 포토스팟) 포함
- 커플 액티비티(서핑 레슨, 요리 클래스, 사진 찍기 좋은 장소 등) 추천
- 로맨틱한 저녁 식사 코스 반드시 포함 (분위기 있는 식당명, 추천 메뉴)
- 붐비는 단체 관광지보다 조용하고 감성적인 장소를 우선`
      : companionType === "family"
      ? `\n[여행 유형: 가족여행]
- 아이와 어른이 함께 즐길 수 있는 장소 최우선 (어린이 입장 가능 여부 반드시 확인)
- 아이 친화적 식당 (키즈 메뉴, 유아 의자, 넓은 공간) 위주로 추천, 식당명과 추천 메뉴 명시
- 어린이 체험 활동 포함 (동물원, 아쿠아리움, 체험 농장, 자연 탐방, 키즈카페 등)
- 긴 이동은 최소화하고 휴식 시간을 충분히 배치 (낮잠/쉬는 시간 포함)
- 유아·어린이 안전을 고려한 장소 선정 (계단 많은 곳, 위험한 액티비티 제외)
- 가족 사진 찍기 좋은 포토스팟 포함 (편안하고 안전한 환경)
- 숙소는 가족실·패밀리룸 가능 여부 언급`
      : companionType === "group"
      ? `\n[여행 유형: 단체여행]
- 여러 명이 함께 즐길 수 있는 넓은 공간, 단체 입장 가능한 명소 우선 추천
- 함께 체험할 수 있는 활동(도자기 체험, 짚라인, 서핑 강습, 요리 체험, 탈출 게임 등) 반드시 포함
- 단체 예약 가능한 식당 (대형 고깃집, 한정식, 뷔페 등) 추천
- 이동이 편리한 동선으로 구성 (대중교통 또는 버스 투어 고려)
- 그룹 사진 찍기 좋은 포토스팟 포함`
      : ""; // solo 또는 미선택 시 기본 프롬프트

    // 경남 목적지일 때 TourAPI 축제 정보를 AI 컨텍스트로 추가
    const isGyenam = GYENAM_CITIES.some(k => (travel.destination||"").includes(k));
    let festivalContext = "";
    if (isGyenam && tourLoaded.festival && tourItems.length > 0) {
      const festivals = tourItems.slice(0,5).map(f => `- ${f.title}${f.eventstartdate ? ` (${f.eventstartdate.slice(0,4)}.${f.eventstartdate.slice(4,6)}.${f.eventstartdate.slice(6,8)})` : ""}${f.addr1 ? ` / ${f.addr1}` : ""}`).join("\n");
      festivalContext = `\n\n[현재 경남 진행 중인 행사/축제 정보 - 일정에 반영 가능하면 포함해주세요]\n${festivals}`;
    }

    const koSystemPrompt = `당신은 한국어 전문 여행 플래너입니다.

【필수 언어 규칙 - 절대 위반 금지】
- 출력 언어: 오직 한국어만 사용
- 중국어(汉字/漢字/简体/繁體) 사용 절대 금지 — 단 한 글자도 허용 안 됨
- 일본어, 영어(고유명사 제외) 사용 금지
- 下午, 晚上, 上午, 旅程, 早晨 등 중국어 시간 표현 사용 금지
- 대신: 오전, 오후, 저녁, 아침, 일정 등 한국어 표현만 사용

【여행 동선 철칙 - 가장 중요】
- Day 1 은 반드시 "출발지에서 즉시 출발"로 시작합니다.
- 출발지에서 관광/식사/체험을 넣지 마세요. 출발지는 오직 "출발" 한 줄만 허용됩니다.
  예: "🌅 오전 · 08:00  서울역 출발 — KTX 탑승 (소요: 약 2시간 40분)"
- 목적지 도착 시각부터 본격적인 여행 일정을 시작합니다 (도시 체크인/환승은 간단히).
- 마지막 Day 는 목적지에서 충분히 즐긴 뒤 귀가 이동으로 마무리합니다.
- 출발지 주변 관광지, 출발지 식당, 출발지 카페는 절대 추천하지 마세요.

【여행 페이스 철칙 - 여유롭고 현실적인 일정】
- 절대 빡빡하게 짜지 마세요. 실제로 따라갈 수 있는 "여유로운" 일정이 목표입니다.
- 장소 간 이동시간을 반드시 현실적으로 반영해서 시각을 계산하세요:
  · 같은 동네 도보 이동: 10~15분
  · 시내 대중교통/택시 이동: 20~30분
  · 도시 외곽·관광지 간 이동: 40~60분 이상
  · 이동수단이 "자가용"이면 주차·진입 시간 10분 추가
  · 이동수단이 "대중교통"이면 환승·배차 대기 시간 10~15분 추가
- 각 장소에 "소요시간"을 명시하고, 다음 장소 시각은 (이전 장소 시작 + 소요 + 이동) 으로 여유 있게 계산.
- 장소 사이에는 5~15분의 "여유 완충(buffer)" 시간을 남겨주세요 (화장실·사진·휴식·간식).
- 식사 시간은 **점심 60~90분, 저녁 90~120분**을 확보 (대기·주문·음식 나오는 시간 포함).
- 하루 일정 개수 가이드 (과밀 금지):
  · 오전: 1~2개 장소
  · 오후: 2~3개 장소
  · 저녁: 1개 장소 + 선택적 야경/산책 1개
  · 하루 총 장소 개수: 최대 5~6개 (이동/식사 제외)
- 오후 2~4시 사이 또는 숙소 체크인 시 **30~60분 휴식/티타임**을 반드시 한 번 포함 ("☕ 여유시간" 또는 "🛋️ 숙소 체크인·휴식").
- 아침 시작 시각은 8:30~10:00 사이 (너무 이른 출발 금지, 단 교통수단 편성상 필요 시 예외).
- 밤 일정 종료는 늦어도 22:00 이전 (야경 관람은 예외적으로 23:00까지).
- 이동이 많은 Day (특히 Day 1 도착일, 마지막 Day 귀가일)에는 장소 수를 1~2개 줄여 여유 확보.

【출력 형식 규칙】
- 시간대별로 구체적인 시각(예: 09:00, 11:30 등)을 반드시 명시
- 각 활동 뒤에 "(소요: N분/시간)" 형식으로 체류시간을 명시
- 장소 간 이동이 있을 땐 "🚶 이동 N분" 또는 "🚗 이동 N분" 줄을 별도로 삽입
- 모든 장소명, 설명, 팁, 예산, 마무리 인사까지 전부 한국어로 작성
- 실제로 따라갈 수 있는 상세한 일정 작성`;

    const originLabel = travel.departure || personal.address || "-";
    const destLabel = travel.destination;

    const prompt = `[중요: 반드시 한국어로만 작성하세요. 중국어 절대 금지]

여행 전문가로서 시간대별로 매우 상세하고 실용적인 여행 일정을 만들어주세요.${companionInstruction}${mbtiInstruction}${personalityMoodInstruction}

${festivalContext}여행자: ${personal.name}(${personal.age}세 ${personal.gender})${profile ? ` / ${profile}` : ""}
출발지: ${originLabel} → 목적지: ${destLabel}
여행기간: ${travelNights}박${travelNights+1}일 (${travel.startDate}~${travel.endDate})${travel.departureTime ? ` / 출발시간: ${travel.departureTime}` : ""}
스타일: ${travel.style} / 이동수단: ${travel.transport}${companionType ? ` / 여행유형: ${{solo:"혼자여행",couple:"커플여행",family:"가족여행",group:"단체여행"}[companionType]}` : ""}

🚨 [동선 규칙 - 반드시 지킬 것]
1) Day 1 첫 일정은 반드시 "${originLabel} 출발" 한 줄입니다. 출발지(${originLabel})에서 관광·카페·식사·체험을 넣지 마세요.
2) Day 1 두 번째 일정부터 "${destLabel}" 도착 이후의 현지 여행 일정으로 채워주세요 (체크인이 있다면 간단히).
3) 마지막 Day 는 "${destLabel}"에서 충분히 즐긴 뒤 "${originLabel}"로 귀가 이동으로 마무리하세요.
4) 출발지 주변 관광지·맛집·카페는 어떤 경우에도 추천·포함하지 마세요.
5) SPOTS_START 배열의 장소 좌표(lat/lng)도 "${destLabel}" 지역에 속해야 합니다 (출발지 장소는 포함 금지).

⏱️ [페이스 규칙 - 절대 빡빡하게 금지]
- 하루 장소 수는 최대 5~6개 (이동·식사 제외). 오전 1~2, 오후 2~3, 저녁 1~2.
- 각 장소 "(소요: N분)" 명시 → 다음 장소 시각 = (시작 + 소요 + 이동시간 + 5~15분 여유).
- 장소 간 이동은 반드시 "🚶 이동 N분" 또는 "🚗 이동 N분" 한 줄로 별도 표기.
- 점심 60~90분, 저녁 90~120분 확보. 아침 시작은 8:30~10:00 권장.
- 오후 2~4시 사이 또는 숙소 체크인 시 30~60분 휴식 ("☕ 여유시간" 또는 "🛋️ 체크인·휴식") 필수.
- 하루 마지막 일정은 22:00 이전 종료 (야경은 예외).
- Day 1(도착일)과 마지막 Day(귀가일)는 장소를 평소보다 1~2개 적게 배치해 여유 확보.

=== ITINERARY_START ===
${profile ? `✦ 성향 한줄 분석\n` : ""}
날짜별 일정 (한국어로만, 간결하게, 여유 있게):

📅 Day 1 (YYYY.MM.DD) - 테마  💡 TIP: 한줄 팁

🌅 오전
• HH:MM  ${originLabel} 출발 — ${travel.transport || "이동수단"} 이용 (소요: 약 N시간)  📍 ${originLabel}역/터미널  💬 팁
🚉 이동 약 N시간
• HH:MM  ${destLabel} 도착 — 숨 고르고 첫 일정 시작

🍽️ 점심 (HH:MM ~ HH:MM, 약 60~90분) — ${destLabel} 현지 식당명, 추천메뉴, 가격대
🚶 이동 N분

🌞 오후
• HH:MM  ${destLabel} 현지 장소명 — 활동 (소요: 약 N분)  📍 위치  💬 팁
🚶 이동 N분
☕ HH:MM  여유시간 / 티타임 (약 30~60분)  💬 잠시 숨 돌리기
🚶 이동 N분
• HH:MM  다른 장소명 — 활동 (소요: 약 N분)  📍 위치

🌙 저녁 (HH:MM ~ HH:MM, 약 90~120분) — ${destLabel} 현지 식당명, 추천메뉴, 가격대

🏨 숙소 (HH:MM) — ${destLabel} 추천 숙소 유형 및 가격대

💰 Day 1 소계: 교통 N,000 / 입장료 N,000 / 식비 N,000 / 합계 약 N만원

📅 Day 2 ... (${destLabel} 현지 일정으로만 구성, 이동시간·여유 포함)

[마지막 Day 는 ${destLabel}에서 오전·점심까지 즐긴 뒤 오후에 ${originLabel}로 귀가 이동]

💰 총 예산: N박N일 1인 약 N만원
💌 마무리 한마디
=== ITINERARY_END ===

=== SPOTS_START ===
[{"name":"장소명","lat":위도,"lng":경도,"dayIndex":0,"dayLabel":"Day 1","description":"한줄설명","order":1}]
=== SPOTS_END ===

[다시 한번 강조: 위 내용을 전부 한국어로만 작성하세요. 중국어 사용 금지]`;

    try {
      let full = await geminiCall(prompt, koSystemPrompt, 4500);
      
      // 중국어 감지 시 재번역 요청
      if (hasChinese(full)) {
        console.warn("중국어 감지 - 한국어 재요청");
        const retryPrompt = `아래 여행 일정이 중국어로 작성되어 있습니다. 이것을 완전히 한국어로만 번역하세요.
중국어 글자 하나도 남기지 말고, 모든 내용을 자연스러운 한국어로 변환하세요.

${full}`;
        const retrySystem = "당신은 번역 전문가입니다. 반드시 한국어로만 번역하세요. 중국어(汉字)는 단 한 글자도 남겨서는 안 됩니다.";
        full = await geminiCall(retryPrompt, retrySystem, 4500);
      }

      const itinMatch = full.match(/=== ITINERARY_START ===([\s\S]*?)=== ITINERARY_END ===/);
      // 마커가 없으면 raw 응답 노출 방지 — 프롬프트/JSON 유출 차단
      const itinText = (() => {
        if (itinMatch) return itinMatch[1].trim();
        // 마커 없이도 일정처럼 보이는 경우: 📅 또는 Day 포함 여부 확인
        const looksLikeItinerary = /📅|Day\s*\d|🌅|🌞|🌙/.test(full);
        if (looksLikeItinerary) {
          // 마커/JSON 블록/시스템 지시문만 제거하고 나머지 사용
          return full
            .replace(/===\s*\w+[\w\s]*===[\s\S]*?===\s*\w+[\w\s]*===/g, "")
            .replace(/```[\s\S]*?```/g, "")
            .replace(/\[{.*?}\]/gs, "")
            .trim();
        }
        return null; // 일정으로 볼 수 없으면 null → 에러 처리
      })();
      if (!itinText) throw new Error("일정 파싱 실패");

      const spotsMatch = full.match(/=== SPOTS_START ===([\s\S]*?)=== SPOTS_END ===/);
      let parsedSpots = [];
      if (spotsMatch) {
        try {
          const raw = spotsMatch[1].replace(/```json|```/gi, "").trim();
          const arr = JSON.parse(raw);
          if (Array.isArray(arr)) {
            parsedSpots = arr
              .filter(p =>
                p && typeof p.lat === "number" && typeof p.lng === "number" &&
                isFinite(p.lat) && isFinite(p.lng) &&
                Math.abs(p.lat) <= 90 && Math.abs(p.lng) <= 180 &&
                !(p.lat === 0 && p.lng === 0)
              )
              .map((p, idx) => {
                // 텍스트 필드에서 HTML/스크립트 태그 제거 (XSS 방지)
                const stripHtml = (s) => String(s||"").replace(/<[^>]*>/g, "").trim();
                return {
                  lat: p.lat,
                  lng: p.lng,
                  order:    (typeof p.order === "number" && p.order > 0) ? p.order : idx + 1,
                  dayIndex: (typeof p.dayIndex === "number" && p.dayIndex >= 0) ? p.dayIndex : 0,
                  dayLabel: stripHtml(p.dayLabel) || `Day ${(p.dayIndex ?? 0) + 1}`,
                  name:     stripHtml(p.name) || `장소 ${idx + 1}`,
                  description: stripHtml(p.description),
                };
              });
          }
        } catch(e) { console.warn("spots parse error", e); }
      }

      clearInterval(timer);
      setProgress(100);

      setItinerary(sanitizeItinerary(itinText));
      setSpots(parsedSpots);
      setSpotsProgress(100);
      setSpotsTotal(parsedSpots.length);
      setSpotsLoading(false);
      setChecklist({ packing:[], before:[], after:[] });
      setTripSaved(false);
      setCommunityShared(false);
      setLinkCopied(false);
      setStep("result");
      // 자동으로 내 여행에 저장 — 상태 업데이트 전이므로 값을 직접 전달
      try { await saveCurrentTrip(sanitizeItinerary(itinText), parsedSpots); } catch(e) { console.warn("자동저장 실패:", e); }
      setResultView("itinerary");
      setView("itinerary");

      try { await upsertRanking(undefined, true); } catch(e) {}

    } catch(err) {
      clearInterval(timer);
      setProgress(0);
      const msg = err?.message || "알 수 없는 오류";
      if (msg === "QWEN_KEY_MISSING") {
        setError("AI 서비스에 연결할 수 없습니다. 로그인 상태를 확인하거나 잠시 후 다시 시도해주세요.");
      } else {
        if (process.env.NODE_ENV === "development") console.error("itinerary error detail:", msg);
        setError("일정 생성에 실패했습니다. 잠시 후 다시 시도해주세요.");
      }
      setStep("travel");
      setSpotsLoading(false);
    }
  }

  const downloadPDF = async () => {
    if (pdfLoading || !itinerary) return;
    setPdfLoading(true);

    // HTML 특수문자 이스케이프 함수
    const esc = (s) => String(s||"").replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;").replace(/"/g,"&quot;");
    // 일정 텍스트를 이모지 제거한 깔끔한 텍스트로 변환
    const cleanLine = (s) => s
      .replace(/[\u{1F300}-\u{1FBFF}\u{2600}-\u{27BF}\u{2300}-\u{23FF}]/gu, "")
      .replace(/[✈✦★☆●◆▲►◀▶]/g, "")
      .trim();

    // HTML 문자열 생성
    const lines = itinerary.split("\n");
    let bodyHTML = "";
    for (const raw of lines) {
      const line = raw.trim();
      if (!line) { bodyHTML += `<div style="height:5px"></div>`; continue; }
      if (line === "---") { bodyHTML += `<hr style="border:none;border-top:1px solid #e8a05055;margin:10px 0">`; continue; }

      const text = line.replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;");

      if (/^📅/.test(line))
        bodyHTML += `<div style="background:linear-gradient(90deg,#fff0e0,#fff8f0);border-left:5px solid #e07840;padding:9px 14px;border-radius:0 8px 8px 0;margin:18px 0 7px;font-size:15px;font-weight:900;color:#c05010">${text}</div>`;
      else if (/날씨:/.test(line) || /^🌤|^☀/.test(line))
        bodyHTML += `<div style="background:#fffbf0;border:1px solid #f0c87055;padding:6px 12px;border-radius:7px;margin:4px 0;font-size:12px;color:#7a5010;font-weight:700">${text}</div>`;
      else if (/^💡/.test(line))
        bodyHTML += `<div style="background:#f4fff0;border:1px dashed #90b84055;padding:5px 12px;border-radius:7px;margin:3px 0 8px;font-size:12px;color:#4a7010">${text}</div>`;
      else if (/^🌅|^🌞|^🌙/.test(line))
        bodyHTML += `<div style="padding:5px 10px;border-left:4px solid #f0a830;margin:11px 0 4px;font-size:13px;font-weight:900;color:#a06020">${text}</div>`;
      else if (/^🍽|^🥘/.test(line))
        bodyHTML += `<div style="background:#fff5ee;border:1px solid #ff8c5033;padding:5px 12px;border-radius:7px;margin:3px 0;font-size:12px;color:#b05020">${text}</div>`;
      else if (/^🚗|^🚇|^🚕|^🚙/.test(line))
        bodyHTML += `<div style="background:#f0f8ff;border:1px solid #4499cc33;padding:4px 12px;border-radius:7px;margin:3px 0;font-size:11px;color:#2060a0">${text}</div>`;
      else if (/^💰|^💌/.test(line))
        bodyHTML += `<div style="background:#fff8e8;border:1px solid #e0a03055;padding:9px 14px;border-radius:8px;margin:12px 0;font-size:13px;font-weight:700;color:#b06010">${text}</div>`;
      else
        bodyHTML += `<div style="padding:2px 10px;font-size:12.5px;color:#4a2810;line-height:1.75">${text}</div>`;
    }

    const tags = [personal.gender, personal.bloodType, personal.mbti,
      personal.zodiac ? personal.zodiac.split(" ")[0] : "", travel.style, travel.transport
    ].filter(Boolean)
     .map(t => `<span style="padding:3px 10px;border-radius:100px;background:#fff4eb;border:1px solid #e07840;font-size:10px;color:#b04010;font-weight:700;display:inline-block">${esc(t)}</span>`)
     .join(" ");

    const today = new Date().toLocaleDateString("ko-KR");
    const filename = esc(personal.name||"여행자") + "_" + esc(travel.destination||"여행") + "_일정.pdf";

    const fullHTML = `<!DOCTYPE html>
<html lang="ko">
<head>
<meta charset="UTF-8">
<title>${esc(filename)}</title>
<style>
  @import url('https://fonts.googleapis.com/css2?family=Noto+Sans+KR:wght@400;700;900&display=swap');
  * { margin:0; padding:0; box-sizing:border-box; }
  body { font-family:'Noto Sans KR','Malgun Gothic','Apple SD Gothic Neo',sans-serif; background:#fff; color:#0F172A; font-size:13px; line-height:1.75; padding:0; }
  #wrap { width:794px; min-height:1122px; padding:36px 40px; background:#fff; }
  @media print {
    html, body { width:794px; }
    #wrap { padding:24px 32px; }
    @page { size:A4 portrait; margin:0; }
  }
</style>
</head>
<body>
<div id="wrap">
  <div style="background:linear-gradient(120deg,#e07840,#f0a830);border-radius:12px;padding:22px 26px;margin-bottom:16px;color:#fff">
    <div style="font-size:22px;font-weight:900;margin-bottom:6px">✈ ${esc(personal.name)||"여행자"}님의 여행 일정</div>
    <div style="font-size:13px;opacity:0.93">${esc(travel.destination)||""} &nbsp;·&nbsp; ${travelNights}박${travelNights+1}일 &nbsp;·&nbsp; ${esc(travel.startDate)||""} ~ ${esc(travel.endDate)||""}</div>
  </div>
  <div style="display:flex;flex-wrap:wrap;gap:6px;margin-bottom:16px">${tags}</div>
  <hr style="border:none;border-top:1.5px solid #e8935a44;margin:0 0 14px">
  ${bodyHTML}
  <div style="margin-top:28px;padding-top:10px;border-top:1px solid #e8c09055;font-size:10px;color:#b08040;display:flex;justify-content:space-between">
    <span>${esc(personal.name)||"여행자"}님의 여행 일정 · TripCanvas</span>
    <span>${today}</span>
  </div>
</div>
</body>
</html>`;

    try {
      // ── 방법 1: html2canvas + jsPDF (동적 CDN 로드) ──
      const loadScript = (src) => new Promise((res, rej) => {
        if (document.querySelector(`script[src="${src}"]`)) { res(); return; }
        const s = document.createElement("script");
        s.src = src; s.onload = res; s.onerror = rej;
        document.head.appendChild(s);
      });

      await loadScript("https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js");
      await loadScript("https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js");

      // 같은 페이지 내 숨김 div에 렌더 (DOMParser로 XSS 방지)
      const div = document.createElement("div");
      div.style.cssText = "position:fixed;left:-9999px;top:0;width:794px;background:#fff;z-index:-1;";
      const parsed = new DOMParser().parseFromString(fullHTML, "text/html");
      const importedNode = document.importNode(parsed.body, true);
      while (importedNode.firstChild) div.appendChild(importedNode.firstChild);
      document.body.appendChild(div);

      // 폰트 로드 대기
      await document.fonts.ready;
      await new Promise(r => setTimeout(r, 800));

      const target = div.querySelector("#wrap") || div;
      const canvas = await window.html2canvas(target, {
        scale: 2, useCORS: true, backgroundColor: "#ffffff",
        width: 794, windowWidth: 794, logging: false,
      });
      document.body.removeChild(div);

      const { jsPDF } = window.jspdf;
      const pdf = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });
      const pageW = 210, pageH = 297;
      const imgW = pageW;
      const imgH = (canvas.height / canvas.width) * imgW;

      let yPos = 0;
      while (yPos < imgH) {
        if (yPos > 0) pdf.addPage();
        pdf.addImage(canvas.toDataURL("image/jpeg", 0.95), "JPEG", 0, -yPos, imgW, imgH);
        yPos += pageH;
      }
      pdf.save(filename);

    } catch (e) {
      console.warn("html2canvas 실패, 인쇄 방식으로 전환:", e);
      // ── 방법 2 폴백: 새 창 열고 인쇄 (PDF로 저장 안내) ──
      const win = window.open("", "_blank", "width=860,height=1050,scrollbars=yes");
      if (!win) { alert("팝업이 차단되었습니다. 팝업 허용 후 다시 시도해주세요."); setPdfLoading(false); return; }
      win.document.write(fullHTML);
      win.document.close();
      // script injection 방지: textContent로 안전하게 삽입
      const printScript = win.document.createElement("script");
      printScript.textContent = `
        window.onload = function() {
          document.title = ${JSON.stringify(filename)};
          setTimeout(function(){ window.print(); }, 800);
          window.onafterprint = function(){ window.close(); };
        };
      `;
      win.document.body.appendChild(printScript);
    }

    setPdfLoading(false);
  };

  const sendChatMessage = async () => {
    const userMsg = chatInput.trim();
    if (!userMsg || chatLoading) return;
    if (!hasRequiredConsents()) {
      await requestRequiredConsent("AI 일정 수정", sendChatMessage);
      return;
    }
    setChatInput("");
    setChatLoading(true);

    const newMessages = [...chatMessages, { role:"user", content:userMsg }];
    setChatMessages(newMessages);
    setTimeout(() => chatBottomRef.current?.scrollIntoView({ behavior:"smooth" }), 50);

    const systemPrompt = `당신은 한국어 전문 여행 일정 수정 전문가입니다.
반드시 한국어로만 답변하세요. 중국어, 영어 등 다른 언어 절대 금지.
아래는 현재 여행자의 정보와 기존 여행 일정입니다.

【여행자 정보】
- 이름: ${personal.name} / 나이: ${personal.age}세 / MBTI: ${personal.mbti}
- 여행지: ${travel.destination} / 기간: ${travel.startDate} ~ ${travel.endDate} (${travelNights}박${travelNights+1}일)
- 여행 스타일: ${travel.style} / 이동 수단: ${travel.transport}${companionType ? ` / 여행유형: ${{solo:"혼자여행",couple:"커플여행",family:"가족여행",group:"단체여행"}[companionType]}` : ""}${companionType === "couple" ? "\n- 커플여행이므로 수정 시에도 로맨틱한 장소, 분위기 좋은 식당·카페를 우선으로 추천해주세요" : companionType === "family" ? "\n- 가족여행이므로 수정 시에도 아이와 어른이 함께 즐길 수 있는 장소, 키즈 메뉴 있는 식당을 우선으로 추천해주세요"
      : companionType === "group" ? "\n- 단체여행이므로 수정 시에도 단체 체험, 여러 명이 즐길 수 있는 넓은 공간을 우선으로 추천해주세요" : ""}

【현재 여행 일정】
${itinerary}

사용자의 수정 요청을 반영하여 일정의 해당 부분만 자연스럽게 변경해주세요.
규칙:
- 수정 요청에 해당하는 부분만 변경하고 나머지는 그대로 유지
- 원래 일정의 이모지, 형식, 구조를 동일하게 유지
- Day 1 첫 일정은 반드시 "${travel.departure || personal.address || "출발지"} 출발" 이동으로 유지 (출발지 관광/식사 추가 금지)
- 출발지 주변 장소·식당·카페는 어떤 경우에도 추가하지 말 것 — 일정의 모든 장소는 "${travel.destination}" 현지여야 함
- 변경된 전체 일정을 === ITINERARY_START === 와 === ITINERARY_END === 사이에 출력
- 변경 내용 요약을 === SUMMARY_START === 와 === SUMMARY_END === 사이에 1~2문장으로 출력`;

    const userPrompt = `수정 요청: ${userMsg}`;

    try {
      const full = await geminiCall(userPrompt, systemPrompt, 3000);

      // Parse updated itinerary — 마커 없으면 기존 일정 유지 (raw 노출 방지)
      const itinMatch = full.match(/=== ITINERARY_START ===([\s\S]*?)=== ITINERARY_END ===/);
      if (itinMatch && itinMatch[1].trim().length > 50) {
        setItinerary(sanitizeItinerary(itinMatch[1].trim()));
      }
      // 마커 없을 경우 기존 itinerary 유지 → 노출 없음

      // Parse summary
      const summaryMatch = full.match(/=== SUMMARY_START ===([\s\S]*?)=== SUMMARY_END ===/);
      const summary = summaryMatch ? summaryMatch[1].trim() : "요청하신 내용을 반영하여 일정을 수정했습니다.";

      setChatMessages(prev => [...prev, { role:"ai", content: summary }]);
    } catch (err) {
      setChatMessages(prev => [...prev, { role:"ai", content:"죄송합니다, 일정 수정 중 오류가 발생했습니다. 다시 시도해주세요." }]);
    } finally {
      setChatLoading(false);
      setTimeout(() => chatBottomRef.current?.scrollIntoView({ behavior:"smooth" }), 100);
    }
  };

  const handleItinerarySpotClick = (spot) => {
    const idx = spots.findIndex(s=>s.name===spot.name);
    if(idx>=0) { setSelectedSpotIdx(idx); setResultView("map"); }
  };

  return {
    // ── 일정/장소 ──
    itinerary, setItinerary,
    progress, setProgress,
    spots, setSpots,
    spotsProgress, setSpotsProgress,
    spotsTotal, setSpotsTotal,
    spotsLoading, setSpotsLoading,
    selectedSpotIdx, setSelectedSpotIdx,
    resultView, setResultView,
    error, setError,

    // ── 일정 수정 채팅 ──
    chatMessages, setChatMessages,
    chatInput, setChatInput,
    chatLoading, setChatLoading,
    chatBottomRef,

    // ── 소셜 공유 ──
    communityShared, setCommunityShared,
    linkCopied, setLinkCopied,
    tripSaved, setTripSaved,

    // ── 저장된 여행 보관함 ──
    savedTrips, setSavedTrips,
    savedTripsLoaded, setSavedTripsLoaded,
    savedTripsError, setSavedTripsError,
    tripSaveError, setTripSaveError,

    // ── PDF ──
    pdfLoading, setPdfLoading,

    // ── 체크리스트 ──
    checklist, setChecklist,
    checklistLoading, setChecklistLoading,
    checklistProgress, setChecklistProgress,

    // ── 경남 추천 (TourAPI) ──
    tourItems, setTourItems,
    tourLoading, setTourLoading,
    tourLoaded, setTourLoaded,
    tourMapPins, setTourMapPins,
    tourError, setTourError,
    tourLoadedRef,

    // ── 함수 ──
    generateItinerary,
    sendChatMessage,
    downloadPDF,
    generateChecklist,
    fetchTourAPI,
    saveCurrentTrip,
    loadSavedTrips,
    deleteSavedTrip,
    loadSavedTripToResult,
    shareToCommunity,
    shareSavedTripToCommunity,
    handleItinerarySpotClick,
  };
}
